import { useEffect, useRef } from "react";

/**
 * R-159 — short-poll callback at a fixed interval. Replaces the previous
 * "operator has to refresh the page to see state changes" UX on the
 * most-visible surfaces (batch list, batch detail, queue dashboard,
 * BatchContextBanner) without waiting for the Phase 10 WebSocket build-out.
 *
 * Polling defaults to paused when the tab is hidden so background tabs
 * don't burn cycles or hit the API with no operator watching. Re-resumes
 * the moment the tab regains visibility (and fires immediately so the
 * operator sees fresh data on tab return rather than waiting up to
 * `intervalMs` for the next tick).
 *
 * Pure hook — no JSX, no store coupling. Callers pass any refresh callback;
 * we keep it in a ref so the interval never holds a stale closure.
 */
export interface UsePollOptions {
  /**
   * Master switch for the poll. Defaults to `true`. Useful for tests, or
   * to pause polling while a destructive flow is in progress on the
   * underlying resource.
   */
  enabled?: boolean;
  /**
   * When true (default), polling pauses on `document.visibilitychange` →
   * hidden, and resumes (plus fires immediately) when visible again.
   * Set false for tests or surfaces that must keep refreshing in the
   * background.
   */
  pauseWhenHidden?: boolean;
}

export function usePoll(
  callback: () => void | Promise<void>,
  intervalMs: number,
  options: UsePollOptions = {},
): void {
  const { enabled = true, pauseWhenHidden = true } = options;
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    let timerId: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      try {
        void savedCallback.current();
      } catch {
        // Swallow — the callback is expected to handle its own errors
        // (every refresh implementation in the app already does).
        // We never want a single failed tick to tear down the interval.
      }
    };

    const start = () => {
      if (timerId != null) return;
      timerId = setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (timerId == null) return;
      clearInterval(timerId);
      timerId = null;
    };

    const handleVisibility = () => {
      if (!pauseWhenHidden) return;
      if (typeof document === "undefined") return;
      if (document.hidden) {
        stop();
      } else {
        // Fire an immediate tick so operator returning to a backgrounded tab
        // sees fresh data rather than waiting up to `intervalMs` for the
        // next interval boundary.
        tick();
        start();
      }
    };

    const hiddenAtMount =
      typeof document !== "undefined" && pauseWhenHidden && document.hidden;
    if (!hiddenAtMount) start();

    if (pauseWhenHidden && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }

    return () => {
      stop();
      if (pauseWhenHidden && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
    };
  }, [enabled, intervalMs, pauseWhenHidden]);
}
